import Const from '@/common/constant.js';

/**
 * PopupQueueManager - Quản lý hàng đợi popup với thứ tự ưu tiên
 * Đảm bảo chỉ 1 popup được hiển thị mỗi lần load/reload trang
 *
 * @author dqduy
 * @created 24/12/2024
 */
class PopupQueueManager {
	constructor() {
		this.STORAGE_KEY = 'popup_queue_status';
		this.userId = null;
		// Định nghĩa thứ tự ưu tiên của các popup
		this.POPUP_QUEUE = [Const.PopupTourguide, Const.PopupNPS, Const.PopupAssistant, Const.PopupFeature];
	}

	/**
	 * Khởi tạo manager với user ID
	 * @param {string} userId - ID của user hiện tại
	 */
	init(userId) {
		this.userId = userId;
	}

	/**
	 * Tạo storage key duy nhất cho mỗi user
	 * @returns {string} Storage key
	 */
	getStorageKey() {
		if (!this.userId) {
			return this.STORAGE_KEY;
		}
		return `${this.STORAGE_KEY}_${this.userId}`;
	}

	/**
	 * Lấy trạng thái các popup đã xem từ localStorage
	 * @returns {Object} Object chứa trạng thái các popup
	 */
	getPopupStatus() {
		try {
			const key = this.getStorageKey();
			const data = localStorage.getItem(key);
			return data ? JSON.parse(data) : null;
		} catch (error) {
			return null;
		}
	}

	/**
	 * Đánh dấu một popup đã được xem
	 * @param {string} popupName - Tên của popup
	 */
	markPopupAsShown(popupName) {
		let status = this.getPopupStatus();
		if (!status) status = {};
		status[popupName] = {
			shown: true,
			timestamp: new Date().toISOString()
		};
		localStorage.setItem(this.getStorageKey(), JSON.stringify(status));
	}

	/**
	 * Kiểm tra một popup đã được xem chưa
	 * @param {string} popupName - Tên của popup
	 * @returns {boolean} true nếu đã xem, false nếu chưa
	 */
	hasShownPopup(popupName) {
		try {
			const status = this.getPopupStatus();
			if (!status || !status[popupName]) return false;
			return status[popupName].shown === true;
		} catch {
			return false;
		}
	}

	/**
	 * Lấy popup tiếp theo cần hiển thị theo thứ tự ưu tiên
	 * @returns {string|null} Tên popup cần hiển thị hoặc null nếu đã xem hết
	 */
	getNextPopupToShow() {
		try {
			for (const popupName of this.POPUP_QUEUE) {
				if (!this.hasShownPopup(popupName)) {
					return popupName;
				}
			}
			return null; // Đã xem hết tất cả popup
		} catch {
			return null;
		}
	}

	/**
	 * Reset queue - xóa toàn bộ trạng thái đã xem
	 * Dùng khi logout hoặc cần reset lại queue
	 */
	reset() {
		localStorage.removeItem(this.getStorageKey());
	}
}

// Export singleton instance
export default new PopupQueueManager();
